"use client";

import { getPdfZoom } from "@/lib/storage";
import { useEffect, useRef, useState } from "react";
import type {
  EventBus,
  PDFFindController,
  PDFLinkService,
  PDFViewer,
} from "pdfjs-dist/web/pdf_viewer.mjs";
import type { PDFDocumentProxy } from "pdfjs-dist";

export type PdfViewerInstance = {
  viewer: PDFViewer;
  eventBus: EventBus;
  linkService: PDFLinkService;
  findController: PDFFindController;
};

/**
 * Manages a single PDFViewer lifecycle attached to the given container ref.
 * Call `setDocument` to swap the displayed PDF without recreating the viewer.
 */
export function usePdfViewer(
  containerRef: React.RefObject<HTMLDivElement | null>,
) {
  const instanceRef = useRef<PdfViewerInstance | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Initialise the viewer once the container mounts.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let resizeObserver: ResizeObserver | undefined;
    let resizeRafId = 0;
    let onPagesInit: (() => void) | undefined;

    // pdfjs-dist/web/pdf_viewer.mjs reads globalThis.pdfjsLib at module
    // evaluation time, so pdfjs-dist must be fully evaluated first.
    // Sequential awaits guarantee the correct order.
    (async () => {
      await import("pdfjs-dist");
      await import("./pdfWorkerSetup");
      await import("pdfjs-dist/web/pdf_viewer.css");
      const viewerMod = await import("pdfjs-dist/web/pdf_viewer.mjs");
      return viewerMod;
    })().then((viewerMod) => {
      if (cancelled) return;

      const eventBus = new viewerMod.EventBus();
      const linkService = new viewerMod.PDFLinkService({ eventBus });
      const findController = new viewerMod.PDFFindController({ eventBus, linkService });
      const viewer = new viewerMod.PDFViewer({
        container,
        eventBus,
        linkService,
        findController,
        textLayerMode: 1,
        annotationMode: 2,
        removePageBorders: false,
      });
      linkService.setViewer(viewer);

      instanceRef.current = { viewer, eventBus, linkService, findController };
      setIsReady(true);

      // After setDocument(), pdf.js resets _currentScaleValue to null
      // internally (_resetView). Restore "page-width" once pages are created,
      // but only when the container is already visible — if the sidebar is
      // still transitioning from 0-width, the ResizeObserver below handles it.
      onPagesInit = () => {
        if (!viewer.currentScaleValue && container.clientWidth > 0) {
          viewer.currentScaleValue = getPdfZoom() ?? "page-fit";
        }
      };
      eventBus.on("pagesinit", onPagesInit);

      // Resize pages when the container size changes.
      // Debounced via rAF to coalesce rapid-fire resize events during initial
      // document load — prevents a cascade of scale changes that can interrupt
      // in-progress annotation layer renders (pdfjs injectLinkAnnotations race).
      // A width-tracking guard breaks the feedback loop: setting scale reflows
      // pages → triggers ResizeObserver → re-applies scale → reflows again.
      // Only re-apply when container width changed by more than 2px to prevent
      // oscillation from scrollbar appear/disappear at the percentage boundary.
      let lastObservedWidth = container.clientWidth;
      resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(resizeRafId);
        resizeRafId = requestAnimationFrame(() => {
          if (container.offsetParent !== null && container.clientWidth > 0) {
            const currentWidth = container.clientWidth;
            if (Math.abs(currentWidth - lastObservedWidth) < 3) return;
            lastObservedWidth = currentWidth;

            if (viewer.currentScaleValue) {
              viewer.currentScaleValue = viewer.currentScaleValue;
            } else if (viewer.pdfDocument && viewer.pagesCount > 0) {
              // Container became visible after setDocument() reset the scale.
              // This happens when the sidebar opens from collapsed (0-width
              // CSS transition) while a document is loading.
              viewer.currentScaleValue = getPdfZoom() ?? "page-fit";
            }
          }
        });
      });
      resizeObserver.observe(container);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(resizeRafId);
      resizeObserver?.disconnect();
      if (instanceRef.current) {
        if (onPagesInit) {
          instanceRef.current.eventBus.off("pagesinit", onPagesInit);
        }
        instanceRef.current.viewer.cleanup();
        instanceRef.current = null;
      }
      setIsReady(false);
    };
  }, [containerRef]);

  return { instanceRef, isReady };
}

/**
 * Swap the document displayed by the viewer.
 * Safe to call repeatedly — if the proxy is the same object, it's a no-op.
 */
export function setViewerDocument(
  instance: PdfViewerInstance | null,
  proxy: PDFDocumentProxy | null,
) {
  if (!instance) return;
  const { viewer, linkService } = instance;

  if (proxy) {
    // Note: page-width is applied via the "pagesinit" listener and
    // ResizeObserver fallback in usePdfViewer — not here.
    // setDocument() internally calls _resetView() which wipes any
    // scale value set before it, so pre-setting is ineffective.
    viewer.setDocument(proxy);
    linkService.setDocument(proxy, null);
  } else {
    // The pdf.js v5 types don't allow null, but the runtime accepts it
    // to clear the current document. Cast to satisfy the compiler.
    viewer.setDocument(undefined as unknown as PDFDocumentProxy);
    linkService.setDocument(undefined as unknown as PDFDocumentProxy, null);
  }
}
